import { useEffect } from "react";
import { api } from "@/lib/api";
import { isCaptured, isSampled, wasExitIntentSeen, markExitIntentSeen } from "@/lib/visitor";
import { useAuth } from "@/context/AuthContext";

/**
 * Exit-intent detector — DISPLAY DISABLED.
 *
 * The modal UI has been intentionally muted while we measure the underlying
 * cohort. The detector still runs and POSTs a "would-have-fired" event to the
 * backend so we can size the potential lead-capture opportunity before deciding
 * whether to re-enable the visible modal. Suppression rules mirror what the
 * live modal would apply — this is not raw mouseleave noise, it's the exact
 * subset that would have seen the modal.
 */
export default function ExitIntent() {
  const { user } = useAuth();
  const mountedAt = Date.now();

  useEffect(() => {
    // Skip anyone who wouldn't have seen the modal anyway
    if (user) return;
    if (isCaptured()) return;
    if (isSampled()) return;
    if (wasExitIntentSeen()) return;

    const onMouseOut = (e) => {
      if (e.clientY > 0 || e.relatedTarget !== null) return;
      // Re-check flags here — they may have flipped since mount
      if (isCaptured() || isSampled() || wasExitIntentSeen()) {
        document.removeEventListener("mouseout", onMouseOut);
        return;
      }
      markExitIntentSeen();
      document.removeEventListener("mouseout", onMouseOut);
      // Fire-and-forget analytics — never block or surface errors to the user
      api.post("/analytics/exit-intent-fired", {
        path: window.location.pathname,
        referrer: document.referrer || null,
        was_logged_in: !!user,
        had_sampled: isSampled(),
        had_captured: isCaptured(),
        session_duration_sec: Math.max(0, Math.round((Date.now() - mountedAt) / 1000)),
      }).catch(() => {});
    };

    const armTimer = setTimeout(() => {
      document.addEventListener("mouseout", onMouseOut);
    }, 8000);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return null;
}
